import { parsePh, type CertFacts } from './facts'
import type { CertRuleSource } from './rule-pack'
import { HACCP_RULE_SOURCES, HALAL_RULE_SOURCES } from './rules.generated'
import { halalMarketIncludes, type CertTarget } from './targets'

/**
 * 인증 준비도 규칙 등록표 (2026-11-01, 설계 3-4). 1차 출처에서 만든 칸(rules.generated.ts)에
 * 손으로 정한 칸 세 가지를 더한다.
 *  - who:    누가 해야 하나. design = 설계(앱·사용자) · factory = 공장 · importer = 수입자 · seller = 판매자(우리) · expert = 전문가
 *  - engine: code = 코드 검사(checks.ts) · ai = 기존 AI 심사 호출 안의 판정 · none = 앱이 못 본다(공장·서류·심사)
 *  - when:   이 제품에 해당하는지. 없으면 그 목표를 고른 모든 제품. 해당하지 않으면 '해당 없음' 개수에만 든다
 * 그리고 hard: 생성 모드에서 코드가 찾으면 다시 만드는 '불합격' 규칙 (설계 3-7. 게이트의 cert-hard).
 *
 * ✅ 는 rules.generated 의 autoCheck 가 '자동' 인 규칙만 될 수 있다 (readiness.ts). '공장 서류 필요'·'사람 확인' 규칙은
 * 설계에 반영했어도 ⚠️ 로 남는다 — 실제 분리·서류·심사는 공장과 인증기관이 해서다.
 */
export type CertWho = 'design' | 'factory' | 'importer' | 'seller' | 'expert'
export type CertEngine = 'code' | 'ai' | 'none'

export interface CertRuleMeta {
  who: CertWho
  engine: CertEngine
  when?: (f: CertFacts) => boolean
  hard?: boolean
  /** 코드 검사 위에 AI 판정도 받는 규칙 (AI 가 fix 면 ❌, unknown 이면 ⚠️) */
  aiAlso?: boolean
}

export interface CertRule extends CertRuleSource, CertRuleMeta {
  target: CertTarget
}

// ── 해당 조건 ────────────────────────────────────────────────────
const my = (f: CertFacts) => halalMarketIncludes(f.halalMarket, 'my')
const id = (f: CertFacts) => halalMarketIncludes(f.halalMarket, 'id')
const ae = (f: CertFacts) => halalMarketIncludes(f.halalMarket, 'ae')
/** 수출 판매국(또는 아직 모름)이라 원료 서류를 요구하는 시장 */
const exportMarket = (f: CertFacts) => f.halalMarket !== 'kr'
const hasHeat = (f: CertFacts) => f.heatSteps.length > 0
const jangNotSoy = (f: CertFacts) => f.isJang
const isSoy = (f: CertFacts) => f.foodKind === 'soy-sauce'
const isSauce = (f: CertFacts) => f.foodKind === 'sauce'
const jangSoySauce = (f: CertFacts) => f.isJang || isSoy(f) || isSauce(f)
/** 멸균 공정이 있거나, 멸균해야 하는 유형(통·병조림·레토르트)인데 살균 공정도 없는 제품 */
const mustCheckSterilization = (f: CertFacts) =>
  f.isSterilized || (f.requiresSterilization && !f.isPasteurized)
/** 1단계(국내)에서는 해당하는 제품이 없는 규칙 — 해외 제조업소·수출용 선택 등록 */
const never = () => false

const HALAL_META: Record<string, CertRuleMeta> = {
  'H-MKT-01': { who: 'factory', engine: 'none', when: my },
  'H-MKT-06': { who: 'factory', engine: 'none', when: (f) => my(f) && f.hasMeat },
  'H-MKT-02': { who: 'importer', engine: 'none', when: id },
  'H-MKT-03': { who: 'design', engine: 'code', when: id },
  'H-MKT-04': { who: 'factory', engine: 'none', when: ae },
  'H-MKT-05': { who: 'factory', engine: 'none', when: ae },
  'H-ING-01': { who: 'design', engine: 'code', hard: true },
  'H-ING-02': { who: 'design', engine: 'code', hard: true },
  'H-ING-03': { who: 'design', engine: 'code', hard: true },
  'H-ING-04': { who: 'design', engine: 'code', hard: true },
  'H-ING-05': { who: 'expert', engine: 'ai' },
  'H-ING-06': { who: 'factory', engine: 'code', when: (f) => f.hasMeat },
  'H-SLA-01': { who: 'factory', engine: 'none', when: (f) => f.hasMeat },
  'H-ALC-01': { who: 'expert', engine: 'code' },
  'H-ALC-02': { who: 'factory', engine: 'none', when: (f) => f.isBeverage },
  'H-ALC-03': { who: 'design', engine: 'ai', when: (f) => f.isFermented },
  'H-ALC-04': { who: 'expert', engine: 'code' },
  'H-SRC-01': { who: 'factory', engine: 'code' },
  'H-SRC-02': { who: 'factory', engine: 'code' },
  'H-SRC-03': { who: 'factory', engine: 'code' },
  'H-SRC-04': { who: 'factory', engine: 'code' },
  'H-SRC-05': { who: 'factory', engine: 'code', when: exportMarket },
  'H-SRC-06': { who: 'factory', engine: 'code' },
  'H-SRC-07': { who: 'factory', engine: 'code', aiAlso: true },
  'H-SRC-08': { who: 'design', engine: 'code', when: id },
  'H-PRC-01': { who: 'factory', engine: 'none' },
  'H-PRC-02': { who: 'factory', engine: 'none' },
  'H-PRC-03': { who: 'factory', engine: 'none' },
  'H-PRC-04': { who: 'factory', engine: 'none' },
  'H-PKG-01': { who: 'factory', engine: 'none' },
  'H-STO-01': { who: 'factory', engine: 'none' },
  'H-LBL-01': { who: 'design', engine: 'code', aiAlso: true },
  'H-LBL-02': { who: 'design', engine: 'ai' },
  'H-LBL-03': { who: 'seller', engine: 'code', when: (f) => f.halalMarket === 'id' },
  'H-LBL-04': { who: 'seller', engine: 'code', when: (f) => f.halalMarket === 'my' },
  'H-DOC-01': { who: 'factory', engine: 'none' },
  'H-DOC-02': { who: 'factory', engine: 'none' },
  'H-DOC-03': { who: 'seller', engine: 'code' },
  'H-DOC-04': { who: 'factory', engine: 'none' },
  'H-DOC-05': { who: 'expert', engine: 'none' },
  // 자동 규칙이지만 공장 인증서(유효기간) 정보가 있어야 판정할 수 있다 — 공장 인증을 구조화하면 코드로 옮긴다 (설계 3-4 끝)
  'H-DOC-06': { who: 'factory', engine: 'none' },
}

const HACCP_META: Record<string, CertRuleMeta> = {
  'HK-SCOPE-01': { who: 'design', engine: 'code', aiAlso: true },
  'HK-SCOPE-02': { who: 'factory', engine: 'none' },
  'HK-SCOPE-03': { who: 'factory', engine: 'none' },
  'HK-SCOPE-04': { who: 'factory', engine: 'none' },
  'HK-SCOPE-05': { who: 'factory', engine: 'none' },
  // 축산물·레토르트 의무는 설계로 볼 것이 없고 공장이 그 업종·유형으로 갖췄는지 서류로 본다 (checks.ts factoryDuty)
  'HK-SCOPE-06': { who: 'factory', engine: 'code', when: (f) => f.isLivestock },
  'HK-SCOPE-07': { who: 'factory', engine: 'code', when: (f) => f.isRetort },
  'HK-SCOPE-08': { who: 'factory', engine: 'none', when: (f) => f.isLivestock },
  'HK-PRP-01': { who: 'factory', engine: 'none' },
  'HK-PRP-02': { who: 'factory', engine: 'none' },
  'HK-PRP-03': { who: 'factory', engine: 'none' },
  'HK-PRP-04': { who: 'factory', engine: 'none' },
  'HK-PRP-05': { who: 'factory', engine: 'none' },
  'HK-PRP-06': { who: 'factory', engine: 'none' },
  'HK-PRP-07': { who: 'factory', engine: 'none', when: hasHeat },
  'HK-PRP-08': {
    who: 'design',
    engine: 'code',
    when: (f) => f.subject.product.storage !== 'ambient',
  },
  'HK-PRP-09': { who: 'factory', engine: 'none' },
  'HK-PRP-10': { who: 'factory', engine: 'none' },
  'HK-PRP-11': { who: 'factory', engine: 'none' },
  'HK-PRP-12': { who: 'factory', engine: 'none' },
  'HK-PRP-13': { who: 'factory', engine: 'none' },
  'HK-PRP-14': {
    who: 'factory',
    engine: 'none',
    when: (f) => f.subject.product.storage !== 'ambient',
  },
  'HK-PRP-15': { who: 'factory', engine: 'none' },
  'HK-PRP-16': { who: 'factory', engine: 'none' },
  'HK-PRP-17': { who: 'design', engine: 'code' },
  'HK-PRP-18': { who: 'factory', engine: 'ai' },
  // 자동 규칙이지만 HACCP 12절차는 공장의 계획서로만 보인다. 초안을 보고 AI 가 ok 라고 하면 근거 없는 ✅ 가 된다
  'HK-PLAN-01': { who: 'factory', engine: 'none' },
  'HK-PLAN-02': { who: 'design', engine: 'code' },
  'HK-PLAN-03': { who: 'expert', engine: 'ai' },
  'HK-PLAN-04': { who: 'expert', engine: 'ai' },
  'HK-PLAN-05': { who: 'design', engine: 'code' },
  'HK-PLAN-06': { who: 'factory', engine: 'none' },
  'HK-PLAN-07': { who: 'factory', engine: 'none' },
  'HK-PLAN-08': { who: 'factory', engine: 'none' },
  'HK-PLAN-09': { who: 'factory', engine: 'none' },
  'HK-PLAN-10': { who: 'factory', engine: 'none' },
  'HK-PLAN-11': { who: 'factory', engine: 'none' },
  'HK-PLAN-12': { who: 'factory', engine: 'code' },
  'HK-CCP-01': {
    who: 'design',
    engine: 'code',
    when: (f) => f.isPasteurized && !f.isDairy,
  },
  'HK-CCP-02': { who: 'design', engine: 'code', when: mustCheckSterilization },
  // 멸균해야 하는 유형(통·병조림·레토르트)을 살균만으로 만들 때 — pH 4.6 이하여야 한다. 그냥 병에 담은 장류는 아니다
  'HK-CCP-03': {
    who: 'design',
    engine: 'code',
    when: (f) => f.isPasteurized && f.requiresSterilization,
  },
  'HK-CCP-04': {
    who: 'design',
    engine: 'code',
    // 저산성(pH 4.6 초과) 통·병조림·레토르트·멸균 제품. pH 를 숫자로 안 적었으면 저산성으로 본다
    when: (f) =>
      (f.isSterilized || f.requiresSterilization) && !((parsePh(f.subject.estPh) ?? 99) <= 4.6),
  },
  'HK-CCP-05': { who: 'design', engine: 'code', when: (f) => f.isRetort, hard: true },
  'HK-CCP-06': {
    who: 'design',
    engine: 'code',
    when: (f) => f.isDairy && (f.isPasteurized || hasHeat(f)),
  },
  'HK-CCP-07': { who: 'design', engine: 'code' },
  'HK-CCP-08': { who: 'design', engine: 'code', when: (f) => f.isPowder && f.hasGrinding },
  'HK-CCP-09': { who: 'factory', engine: 'none' },
  'HK-CCP-10': { who: 'design', engine: 'code', when: hasHeat },
  'HK-CCP-11': { who: 'design', engine: 'code', when: (f) => f.hasThaw },
  'HK-CCP-12': { who: 'factory', engine: 'none' },
  'HK-STO-01': { who: 'design', engine: 'code' },
  // 설계는 AI 판정이지만, 실온 + 소비기한 7일 미만은 칸 두 개로 셀 수 있어 코드가 본다
  'HK-STO-02': { who: 'design', engine: 'code' },
  'HK-STO-03': { who: 'design', engine: 'code' },
  'HK-STO-04': { who: 'design', engine: 'code', when: (f) => f.coldChainType },
  'HK-STD-01': { who: 'design', engine: 'code', when: jangNotSoy, hard: true },
  'HK-STD-02': { who: 'design', engine: 'code', when: jangNotSoy, hard: true },
  'HK-STD-03': {
    who: 'design',
    engine: 'code',
    when: (f) => f.foodKind === 'gochujang',
    hard: true,
  },
  'HK-STD-04': {
    who: 'design',
    engine: 'code',
    when: (f) => f.foodKind === 'gochujang',
    hard: true,
  },
  'HK-STD-05': {
    who: 'design',
    engine: 'code',
    when: (f) => f.foodKind === 'mixed-jang',
    hard: true,
  },
  'HK-STD-06': { who: 'design', engine: 'code', when: isSoy, hard: true },
  'HK-STD-07': { who: 'design', engine: 'code', when: isSauce, hard: true },
  'HK-STD-08': {
    who: 'design',
    engine: 'code',
    when: (f) => f.goals.targets.includes('halal') && jangSoySauce(f),
  },
  // 그 밖의 식품유형 개별 기준표(지식코어 std_indiv_spec 16,483 규칙)는 아직 코드에 넣지 않았다 — 전문가 확인으로 둔다
  'HK-STD-09': { who: 'expert', engine: 'none', when: (f) => !jangSoySauce(f) },
  'HK-AUD-01': { who: 'factory', engine: 'none' },
  'HK-AUD-02': { who: 'factory', engine: 'none' },
  'HK-AUD-03': { who: 'factory', engine: 'none' },
  'HK-AUD-04': { who: 'factory', engine: 'none' },
  'HK-AUD-05': { who: 'factory', engine: 'none' },
  'HK-AUD-06': { who: 'factory', engine: 'none' },
  'HK-AUD-07': { who: 'factory', engine: 'none' },
  'HK-AUD-08': { who: 'factory', engine: 'none' },
  'HK-AUD-09': { who: 'factory', engine: 'none' },
  'HK-LBL-01': { who: 'design', engine: 'code' },
  'HK-LBL-02': { who: 'design', engine: 'code' },
  'HK-LBL-03': { who: 'factory', engine: 'none' },
  'HK-OEM-01': { who: 'seller', engine: 'none' },
  'HK-SUP-01': { who: 'factory', engine: 'none' },
  'HK-SUP-02': {
    who: 'factory',
    engine: 'none',
    when: (f) => f.subject.product.categoryId === 'processed-meat',
  },
  'HK-SUP-03': { who: 'factory', engine: 'none', when: never },
  'HK-GH-01': { who: 'factory', engine: 'none', when: never },
}

function merge(target: CertTarget, sources: CertRuleSource[], meta: Record<string, CertRuleMeta>) {
  return sources.map((source): CertRule => {
    const m = meta[source.id]
    if (!m) throw new Error(`cert rule ${source.id} has no meta`)
    return { ...source, ...m, target }
  })
}

/** 목표별 규칙 (팩 순서 그대로) */
export const CERT_RULES: Record<'halal' | 'haccp', CertRule[]> = {
  halal: merge('halal', HALAL_RULE_SOURCES, HALAL_META),
  haccp: merge('haccp', HACCP_RULE_SOURCES, HACCP_META),
}

export function certRule(id: string): CertRule | undefined {
  return CERT_RULES.halal.find((r) => r.id === id) ?? CERT_RULES.haccp.find((r) => r.id === id)
}

/** 메타가 팩에 없는 id 를 가리키지 않는지 (테스트용) */
export const CERT_META_IDS = [...Object.keys(HALAL_META), ...Object.keys(HACCP_META)]
